import React, { useRef, useState } from 'react'
import { useMeetingTypeHeader } from '@chilipiper/api-queries/src/meeting-type'
import { Flex, Text } from '@chilipiper/design-system'
import { useGetOrderedListOfUsers } from '@chilipiper/distribution-components'
import {
  GuestFormField,
  MapLocaleGuestFormLocalization,
  MeetingInvite,
  PublicMeetingType,
  GuestFormQuestion,
} from '@chilipiper/api-client/src/meeting-types'
import * as v from 'valibot'
import {
  MeetingAttempt,
  MeetingAttemptResponse,
  ProductFeature,
  SuccessfullyReserved,
} from '@chilipiper/api-client/src/user-availability'
import invariant from 'tiny-invariant'
import { getClientTimezone } from '@chilipiper/date-time'
import { ProductFeatureWithRoundRobin } from '@chilipiper/api-client/src/distribution'
import { partition } from 'remeda'
import {
  ScheduleMeetingRequest,
  OneClickScheduleRequest,
  OneClickScheduleResponse,
  MapDataFieldRefString,
  SetSecondaryFeature,
} from '@chilipiper/api-client/src/team-scheduling'
import { SuggestedTimesBlock } from '../../../chat-data/types'
import { useMessageFeedContext } from '../../../chat-widget/MessageFeedContext'
import {
  useCancelMeetingAttemptReservation,
  useClaimMeetingAttemptReservation,
  useMeetingAttempt,
} from '../../../api-queries/userAvailability'
import {
  useBookMeeting,
  useHandoff,
  useHandoffBooking,
  useOneClickBooking,
} from '../../../api-queries/meetingsPlatform'
import { useRoundRobinSchedulingLink } from '../../../api-queries/team-scheduling'
import { useDataFields } from '../../../api-queries/chiliCrm'
import { TranslationProvider } from '../../../context/TranslationContext'
import { BookingFlowBlock } from './BookingFlowBlock'
import { StyledBookMeetingContainer } from './styles'
import { useLoadMeetingAttemptRelatedData } from './hooks'
import {
  getGuestEmail,
  getHandoffStrictAssigneeId,
  getHandoffWithReservationPayload,
  getMeetingAttemptReservationPayload,
  getMeetingRequestFeature,
  getMeetingRequestFeatureWithGuestFormData,
  getRoundRobinSchedulingLinkId,
  getSuggestedTimeRouting,
  mapMeetingAttendees,
  mapQuestionDataTypeToDataFieldDataType,
  ErrorSchema,
  GuestFormValues,
} from './helpers'
import { BookingFlowBlockLoading } from './BookingFlowBlockLoading'

interface Props {
  block: SuggestedTimesBlock
}

export const SuggestedTimes = ({ block }: Props) => {
  const { tenantId, workspaceId = '' } = useMessageFeedContext()
  const { data: meetingAttempt, isLoading: isMeetingAttemptLoading } = useMeetingAttempt({
    tenantId,
    meetingAttemptId: block.payload.attemptId,
  })
  const [isRescheduling, setIsRescheduling] = useState(false)
  const productFeatureType = getProductFeatureTypeWithRoundRobin(meetingAttempt)
  const now = useRef(new Date())
  const [optimisticMeetingId, setOptimisticMeetingId] = useState<string>()

  const { meetingType, meetingTypeId } = useMeetingType(tenantId, block, meetingAttempt)

  const {
    cancelMeetingAttemptReservation,
    claimMeetingAttemptReservation,
    isAttemptingReservation,
    isMeetingReserved,
    claimMeetingAttemptReservationError,
  } = useReservation(tenantId, block)

  const {
    bookMeetingMutation,
    isBookingMeetingMutation,
    isLoadingOneClickBooking,
    oneClickBookMeeting,
    oneClickBookingData,
    bookingError,
  } = useBooking(tenantId)
  const { mutate: handleHandoffScheduleMeeting, isPending: isLoadingHandoffBooking } =
    useHandoffBooking(tenantId)
  const [guestFormFilledData, setGuestFormFilledData] = useState({})
  const { formFields } = useGuestFormFields(
    meetingType,
    guestFormFilledData,
    oneClickBookingData,
    bookingError
  )
  const alwaysShowForm = meetingType?.guestForm.isAlwaysShowFormEnabled

  const { data: roundRobinSchedulingLink, isLoading: isInitialLoadingRoundRobinSchedulingLink } =
    useRoundRobinSchedulingLink(tenantId, getRoundRobinSchedulingLinkId(meetingAttempt))

  const routingId = getRoutingIdFromMeetingAttempt(meetingAttempt)
  const {
    handoffRouting,
    refetchPublicRoutingInfo,
    distributionId: handoffDistributionId,
    isLoading: isPublicRoutingInfoLoading,
  } = useHandoff(tenantId, routingId)

  const distributionId = handoffDistributionId ?? roundRobinSchedulingLink?.distributionId
  const { data: distribution, isInitialLoading: isInitialLoadingDistribution } =
    useGetOrderedListOfUsers(tenantId, distributionId, {
      productFeature: productFeatureType,
    })

  const {
    timeSlots,
    assigneeOptions,
    additionalRequiredAttendees,
    refetchAvailability,
    isLoading: isLoadMeetingAttemptRelatedData,
  } = useLoadMeetingAttemptRelatedData({
    meetingAttempt,
    distribution,
    distributionId,
    isMeetingAttemptLoading,
    handoffRouting,
    meetingTypeId,
    now: now.current,
    isRescheduling,
  })

  const assigneesIds = assigneeOptions?.combinedAvailability
    ? [assigneeOptions.hostId, ...additionalRequiredAttendees]
    : assigneeOptions?.assigneeIds

  if (!meetingAttempt || !meetingType) {
    return (
      <BookingFlowBlockLoading
        state={(() => {
          if (!meetingAttempt) {
            return undefined
          }
          if (meetingAttempt.type === 'Booked') {
            return 'scheduled'
          }
          return 'scheduling'
        })()}
      />
    )
  }

  if (meetingAttempt.type === 'NotFound') {
    return (
      <StyledBookMeetingContainer w='100%' flexDirection='column' borderRadius={8}>
        <Flex p={7} my={1} justifyContent='center' alignItems='center'>
          <Text color='fg/caution' ml={2} fontWeight={500}>
            Failed to load suggested times
          </Text>
        </Flex>
      </StyledBookMeetingContainer>
    )
  }
  const meetingInvite: MeetingInvite = {
    title: meetingAttempt.attempt.meetingInvite.titleOverride ?? meetingType.meetingInvite.title,
    description:
      meetingAttempt.attempt.meetingInvite.descriptionOverride ??
      meetingType.meetingInvite.description,
    duration:
      meetingAttempt.attempt.meetingInvite.meetingDurationOverride ??
      meetingType.meetingInvite.duration,
    locations:
      meetingAttempt.attempt.meetingInvite.possibleLocationsOverride ??
      meetingType.meetingInvite.locations,
  }

  const handleHandoffBookMeeting = (
    meetingAttempt: MeetingAttempt,
    reservation: SuccessfullyReserved
  ) => {
    const handoffRoutingId =
      meetingAttempt.productFeature.type === 'HandoffRouting'
        ? meetingAttempt.productFeature.routingId
        : undefined

    invariant(
      meetingType && handoffRouting && reservation.meetingReservation.id && handoffRoutingId,
      'Missing required data for Handoff scheduling meeting with reservation'
    )

    const assigneeId = getHandoffStrictAssigneeId(handoffRouting.selectResponse.selectedAssignee)

    const payload = getHandoffWithReservationPayload({
      reservationId: reservation.meetingReservation.id,
      assigneeId,
      location: meetingType.meetingInvite.locations.default,
    })

    handleHandoffScheduleMeeting(
      {
        routingId: handoffRoutingId,
        params: payload,
      },
      {
        onSuccess: response => {
          if (response.type === 'ScheduledByReservation') {
            setOptimisticMeetingId(response.meetingId)
          }
        },
        onError: () => {
          cancelMeetingAttemptReservation({
            reservationId: reservation.meetingReservation.id,
          })
        },
      }
    )
  }

  const getCrmExplicits = (productFeature: ProductFeature) => {
    if (productFeature.type === 'HandoffRouting') {
      return undefined
    }
    return productFeature.crmExplicits
  }

  const getCrmId = (productFeature: ProductFeature) => {
    if (productFeature.type === 'HandoffRouting') {
      return undefined
    }
    return productFeature.primaryGuestV2?.crmId
  }

  const handleBookMeeting = async ({
    selectedTime,
    guestFormData,
    onError,
  }: {
    guestFormData: GuestFormValues
    onError: () => void
    selectedTime: string
    timezone: string
  }) => {
    setGuestFormFilledData(guestFormData)
    const guestFormEmail = guestFormData['PersonEmail']
    const email = Array.isArray(guestFormEmail) ? guestFormEmail[0] : guestFormEmail
    invariant(email, 'Email is required to book a meeting')
    const payload = getMeetingAttemptReservationPayload({
      meetingAttempt,
      meetingTypeRef: {
        id: meetingTypeId,
        timestamp: new Date().toISOString(),
      },
      slotStartsAt: selectedTime,
      distribution,

      routing: getSuggestedTimeRouting(handoffRouting),
    })
    const reservation = payload
      ? await claimMeetingAttemptReservation({
          ...payload,
          meetingLimit: {
            //TODO: get source meeting limit
            type: 'NoMeetingLimit',
          },
          availabilityChecks: {
            type: 'All',
          },
        })
      : undefined

    const productFeature = meetingAttempt.attempt.productFeature

    if (reservation?.type === 'AlreadyReserved' || reservation?.type === 'AlreadyBooked') {
      return onError()
    }
    if (reservation && productFeature.type === 'HandoffRouting') {
      return handleHandoffBookMeeting(meetingAttempt.attempt, reservation)
    }

    const feature = getMeetingRequestFeature({
      productFeature,
      reservationId: reservation?.meetingReservation.id,
      distribution,
      distributionId,
      duration: meetingInvite.duration,
      slotStartAt: selectedTime,
      assigneesIds,
    })
    if (!feature) {
      return onError()
    }

    const commonData: Pick<
      ScheduleMeetingRequest,
      'primaryGuest' | 'attendees' | 'meetingTypeOverride' | 'crmExplicits'
    > = {
      primaryGuest: {
        type: 'External',
        email,
        timezone: getClientTimezone(),
        crmId: getCrmId(meetingAttempt.attempt.productFeature),
      },
      attendees: mapMeetingAttendees(meetingAttempt.attempt.additionalAttendees),
      crmExplicits: getCrmExplicits(meetingAttempt.attempt.productFeature),
      meetingTypeOverride: {
        meetingInvite: {
          description: {
            type: meetingInvite.description ? 'Change' : 'LeaveAsIs',
            value: meetingInvite.description,
          },
          title: {
            type: meetingInvite.title ? 'Change' : 'LeaveAsIs',
            value: meetingInvite.title,
          },
        },
        buffers: {
          before: { type: 'LeaveAsIs' },
          after: { type: 'LeaveAsIs' },
        },
        reminder: {
          reminders: {
            type: 'LeaveAsIs',
          },
        },
      },
    } as const
    if (alwaysShowForm || oneClickBookingData?.type === 'IncompleteForm') {
      const featureWithGuestForm = getMeetingRequestFeatureWithGuestFormData(
        feature,
        guestFormData,
        meetingType
      )
      const data: ScheduleMeetingRequest = {
        ...commonData,
        feature: {
          ...featureWithGuestForm,
          secondaryFeature: [] as SetSecondaryFeature,
          uiComponent: 'ChatWidget',
          meetingAttemptId: meetingAttempt.attempt.id,
        },
        location: {
          type: meetingType.meetingInvite.locations.default.type,
        },
      }
      bookMeetingMutation(data, {
        onSuccess: response => {
          if (response.type === 'Accepted') {
            setOptimisticMeetingId(response.meetingId)
          }
        },
        onError: () => {
          if (reservation?.meetingReservation.id) {
            cancelMeetingAttemptReservation({
              reservationId: reservation.meetingReservation.id,
            })
          }
        },
      })
    } else {
      const data: OneClickScheduleRequest = {
        ...commonData,
        meetingTypeReference: {
          meetingTypeId: timeSlots?.meetingTypeRef?.id ?? meetingTypeId,
          timestamp: timeSlots?.meetingTypeRef?.timestamp ?? new Date().toISOString(),
        },
        feature,
        meetingAttemptId: meetingAttempt.attempt.id,
        uiComponent: 'ChatWidget',
        secondaryFeature: ['OneClickBooking'] as SetSecondaryFeature,
        partialGuestData: {
          values: Object.entries(guestFormData).reduce((acc, [key, v]) => {
            const value = Array.isArray(v) ? v[0] : v
            if (value) {
              acc[key] = value
            }
            return acc
          }, {} as MapDataFieldRefString),
        },
      }
      oneClickBookMeeting(data, {
        onSuccess: response => {
          if (
            (response.type === 'IncompleteForm' ||
              response.type === 'NoAvailableAssignees' ||
              response.type === 'ProspectLimitReached') &&
            reservation
          ) {
            cancelMeetingAttemptReservation({
              reservationId: reservation.meetingReservation.id,
            })
          }
          if (response.type === 'Accepted') {
            setOptimisticMeetingId(response.meetingId)
          }
        },
      })
    }
  }

  return (
    <TranslationProvider tenantId={tenantId} workspaceId={workspaceId}>
      <BookingFlowBlock
        isRescheduling={isRescheduling}
        onReschedulingChange={setIsRescheduling}
        isFlexibleReassignment={distribution?.reassignmentType === 'AnyTeamMember'}
        suggestedTimes={block.payload.slots.map(slot => slot.start)}
        fields={(() => {
          if (oneClickBookingData === undefined && !alwaysShowForm) {
            const emailField = formFields.find(field => field.id === 'PersonEmail')
            const hiddenFields = formFields
              .filter(field => field.hiddenValue !== undefined)
              .map(field => ({ ...field, error: undefined }))
            if (!emailField) {
              return hiddenFields
            }
            return [{ ...emailField, error: undefined }, ...hiddenFields]
          }
          return formFields
        })()}
        block={{
          meetingTypeId,
          author: block.author,
          roomId: block.roomId,
          sent: block.sent,
          statuses: block.statuses,
          timestamp: block.timestamp,
          type: 'BookMeeting',
          id: block.id ?? '',
          meetingId:
            meetingAttempt.type === 'Booked' ? meetingAttempt.meetingId : optimisticMeetingId,
        }}
        claimMeetingAttemptReservationError={claimMeetingAttemptReservationError}
        meetingInvite={meetingInvite}
        onBookMeeting={handleBookMeeting}
        isMeetingReserved={
          isMeetingReserved &&
          oneClickBookingData?.type !== 'IncompleteForm' &&
          oneClickBookingData?.type !== 'NoAvailableAssignees' &&
          bookingError === undefined
        }
        isNoAvailableAssignee={oneClickBookingData?.type !== 'NoAvailableAssignees'}
        isBookingMeeting={
          isBookingMeetingMutation ||
          isAttemptingReservation ||
          isLoadingOneClickBooking ||
          isLoadingHandoffBooking
        }
        assigneesIds={assigneesIds}
        guestEmail={getGuestEmail(meetingAttempt.attempt.productFeature, handoffRouting)}
        timeSlots={timeSlots}
        refetchAvailability={() => {
          if (routingId) {
            void refetchPublicRoutingInfo()
          }
          void refetchAvailability()
        }}
        error={
          oneClickBookingData?.type === 'ProspectLimitReached'
            ? 'Prospect Limit Reached'
            : undefined
        }
        isLoading={
          isLoadMeetingAttemptRelatedData ||
          isPublicRoutingInfoLoading ||
          isInitialLoadingDistribution ||
          isInitialLoadingRoundRobinSchedulingLink
        }
        initialTimezone={
          meetingType?.forceGuestTimezone.type === 'Enabled'
            ? meetingType.forceGuestTimezone.zoneId
            : undefined
        }
      />
    </TranslationProvider>
  )
}

const getRoutingIdFromMeetingAttempt = (meetingAttempt?: MeetingAttemptResponse) => {
  if (
    meetingAttempt?.type === 'Available' &&
    meetingAttempt.attempt.productFeature.type === 'HandoffRouting'
  ) {
    return meetingAttempt.attempt.productFeature.routingId
  }

  return
}

const getFieldText = (fieldLocalization: MapLocaleGuestFormLocalization) => {
  if (fieldLocalization['en-US']) {
    return fieldLocalization['en-US']
  }
  return (
    Object.entries(fieldLocalization).find(([key, value]) => {
      if (key.startsWith('en')) {
        return value
      }
    })?.[1] ?? Object.values(fieldLocalization)[0]
  )
}

const useGuestFormFields = (
  meetingType: PublicMeetingType | undefined,
  guestFormFilledData: Record<string, string>,
  oneClickBookingData: OneClickScheduleResponse | undefined,
  bookingError: unknown
) => {
  const { tenantId } = useMessageFeedContext()

  const getFields = () => {
    const mapField = (field: GuestFormQuestion | GuestFormField) => {
      const id = 'id' in field ? field.id : field.reference
      const initialValue = guestFormFilledData[id]
      const { error, label, description } = getFieldText(field.localizations) ?? {}
      const isRequired =
        field.type === 'Field' ? field.requirement.type === 'Required' : field.isRequired

      const getHiddenValue = (field: GuestFormField | GuestFormQuestion) => {
        if (field.type === 'Field' && field.requirement.type === 'Hidden') {
          return field.requirement.value
        }
        return undefined
      }
      return {
        id,
        isRequired,
        label,
        description,
        error: isRequired && !initialValue ? (error ?? 'This value is required') : undefined,
        initialValue,
        hiddenValue: getHiddenValue(field),
        dataType:
          'id' in field ? mapQuestionDataTypeToDataFieldDataType(field.dataType) : undefined,
      }
    }
    const getAllFields = () => {
      if (!meetingType) {
        return []
      }

      if (meetingType.guestForm.type === 'DataFields') {
        return meetingType.guestForm.fields.map(mapField)
      }

      if (meetingType.guestForm.type === 'Mixed') {
        return meetingType.guestForm.items.map(mapField)
      }
      return []
    }

    const getFormInvalidError = (bookingError: unknown) => {
      const { success, output } = v.safeParse(ErrorSchema, bookingError)
      if (success && output.error.errorCode === 'meetings-api-service.form-validation-failed') {
        return output
      }
      return null
    }

    const allFields = getAllFields() ?? []
    if (meetingType?.guestForm.isAlwaysShowFormEnabled) {
      return allFields
    }
    if (oneClickBookingData?.type === 'IncompleteForm') {
      return allFields
      // todo: currently we don't receive all missing fields in partialData.missing will be fixed here https://floatingapps.atlassian.net/browse/CPP-19036
      // const missingFields = oneClickBookingData.partialData.missing.map(field =>
      //   'id' in field ? field.id : field.reference
      // )
      // return allFields?.filter(field => missingFields.includes(field.id))
    }
    const hiddenFields = allFields.filter(field => field.hiddenValue !== undefined)
    // todo: once we start receiving bookingData as we do for oneClickBookingData we should remove this condition
    const formInvalidError = getFormInvalidError(bookingError)
    if (formInvalidError || hiddenFields.length > 0) {
      return allFields.filter(field => {
        return (
          formInvalidError?.error.errorMessage.includes(field.id) ||
          hiddenFields.some(hiddenField => hiddenField.id === field.id)
        )
      })
    }

    return hiddenFields
  }

  const [questions, fields] = partition(getFields(), field => !!field.dataType)

  const { data: dataFields } = useDataFields(tenantId, fields?.map(field => field.id) ?? [])
  const formFields = fields
    ?.map(field => {
      const dataField = dataFields?.elements?.find(dataField => dataField.reference === field.id)
      return {
        ...field,
        dataType: field.dataType ?? dataField?.dataType,
      }
    })
    .concat(questions ?? [])
  return { formFields }
}

const getProductFeatureTypeWithRoundRobin = (
  meetingAttempt?: MeetingAttemptResponse
): ProductFeatureWithRoundRobin | undefined => {
  const routingProductFeatureTypes: Record<string, ProductFeatureWithRoundRobin> = {
    RoundRobinSchedulingLink: 'RoundRobinSchedulingLink',
    HandoffRouting: 'HandoffRouter',
    ConciergeRouter: 'ConciergeRouter',
    ChatPlaybook: 'ChatPlaybook',
    DistroRouter: 'DistroRouter',
  }

  if (!meetingAttempt || meetingAttempt.type === 'NotFound') {
    return
  }

  if (meetingAttempt.type === 'Available') {
    const productFeatureType = meetingAttempt.attempt.productFeature.type
    return Object.keys(routingProductFeatureTypes).includes(productFeatureType)
      ? routingProductFeatureTypes[productFeatureType]
      : undefined
  }
}

const getMeetingTypeId = (blockMeetingType: string, meetingAttempt?: MeetingAttemptResponse) => {
  let roundRobinSchedulingLinkSelectedMeetingType
  if (
    (meetingAttempt?.type === 'Available' || meetingAttempt?.type === 'Booked') &&
    meetingAttempt?.attempt.productFeature.type === 'RoundRobinSchedulingLink'
  ) {
    roundRobinSchedulingLinkSelectedMeetingType =
      meetingAttempt.attempt.productFeature.selectedMeetingTypeId
  }
  return roundRobinSchedulingLinkSelectedMeetingType
    ? roundRobinSchedulingLinkSelectedMeetingType
    : blockMeetingType
}

const useMeetingType = (
  tenantId: string,
  block: SuggestedTimesBlock,
  meetingAttempt?: MeetingAttemptResponse
) => {
  const meetingTypeId = getMeetingTypeId(block.payload.meetingTypeId, meetingAttempt)
  const { data: meetingType } = useMeetingTypeHeader(tenantId, meetingTypeId)
  return { meetingType, meetingTypeId }
}

const useBooking = (tenantId: string) => {
  const {
    mutate: bookMeetingMutation,
    isPending: isBookingMeetingMutation,
    error: bookingError,
    data: bookingData,
  } = useBookMeeting(tenantId)
  const {
    mutate: oneClickBookMeeting,
    isPending: isLoadingOneClickBooking,
    data: oneClickBookingData,
  } = useOneClickBooking(tenantId)

  return {
    bookMeetingMutation,
    bookingError,
    bookingData,
    isBookingMeetingMutation,
    isLoadingOneClickBooking,
    oneClickBookMeeting,
    oneClickBookingData,
  }
}

const useReservation = (tenantId: string, block: SuggestedTimesBlock) => {
  const { mutate: cancelMeetingAttemptReservation } = useCancelMeetingAttemptReservation({
    tenantId,
    meetingAttemptId: block.payload.attemptId,
  })
  const {
    mutateAsync: claimMeetingAttemptReservation,
    isPending: isAttemptingReservation,
    error: claimMeetingAttemptReservationError,
    isSuccess: isMeetingReserved,
  } = useClaimMeetingAttemptReservation({
    tenantId,
    meetingAttemptId: block.payload.attemptId,
  })
  return {
    cancelMeetingAttemptReservation,
    claimMeetingAttemptReservation,
    isAttemptingReservation,
    isMeetingReserved,
    claimMeetingAttemptReservationError,
  }
}
